import React, { useState } from "react";
import { observer } from "mobx-react";
import { navigate } from "@reach/router";
import { FiHash } from "react-icons/fi";
import { useBreakpoints } from "../../../hooks/useBreakpoints";
import useOnEnterPressed from "../../../hooks/useOnEnterPressed";
import AuthStore from "../../../stores/AuthStore";
import { Input, Spinner, Button } from "../../-common";
import "./VerifyAccount.scss";

const VerifyAccount = observer(({ location }) => {
  const { isMobile } = useBreakpoints();

  const { email, fromManualEntry } = location?.state || {};
  
  const [code, setCode] = useState("");
  const updateCode = ({ target }) => {
    setError();
    setCode(target.value);
  };

  const [error, setError] = useState();
  const [successMessage, setSuccessMessage] = useState("");

  const verificationEnabled = code?.length === 6 && Number(code);
  const attemptVerification = async () => {
    if (verificationEnabled) {
      setError();
      const { success, alreadyConfirmed, error: errorFromStore } = await AuthStore.verifyAccount(code);
      if (success) {
        // If email was manually entered (not from signup flow), always redirect to login
        if (fromManualEntry) {
          setSuccessMessage("Account verified! Please log in with your credentials.");
          setTimeout(() => {
            navigate("/auth");
          }, 2000);
        } else if (AuthStore?.authenticated) {
          // Normal signup flow with auto-login
          setSuccessMessage("Account verified successfully! Redirecting...");
          setTimeout(() => {
            navigate("/students");
          }, 1500);
        } else {
          // Verification succeeded but auto-login failed
          setSuccessMessage("Account verified! Please log in with your credentials.");
          setTimeout(() => {
            navigate("/auth");
          }, 2000);
        }
      } else if (alreadyConfirmed) {
        setSuccessMessage("Account already verified! Redirecting to login...");
        setTimeout(() => {
          navigate("/auth");
        }, 1500);
      } else {
        setError(errorFromStore);
      }
    }
  };
  useOnEnterPressed(attemptVerification);

  const resendCode = async () => {
    const { success } = await AuthStore?.resendVerificationCode();
    if (success) {
      setSuccessMessage("Verification code resent! Check your email.");
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  };

  const instructions = (
    <div className="right-col">
      <h1 className="hero-text">Almost there!</h1>
      {email ? (
        <>
          <p className="helper-text">We've sent your verification code to {email}</p>
          <p className="helper-text">Enter your verification code and we'll get you right on to setting up your kids.</p>
        </>
      ) : (
        <>
          <p className="helper-text">Enter the verification code that was sent to your email address.</p>
          <p className="helper-text">If you didn't receive a code, you can click "Resend Code" below.</p>
        </>
      )}
    </div>
  );

  const buttonContents = AuthStore.loading ? <Spinner /> : "Verify";
  const buttonStyles = AuthStore.loading ? { opacity: 0.7, pointerEvents: "none" } : {};

  return (
    <div className="verify-account">
      {isMobile ? instructions : null}
      <div className="left-col">
        <div className="verify-content">
          <div className="verify-square">
            <div className="verify-inputs">
              <Input
                icon={FiHash}
                type="text"
                placeholder="Verification Code"
                value={code}
                onChange={updateCode}
                error={error}
                pattern="[0-9]*"
                inputMode="numeric"
              />
              {successMessage && (
                <div className="success-message" style={{ 
                  color: "#4CAF50", 
                  textAlign: "center", 
                  marginTop: "16px",
                  fontWeight: "600",
                  fontSize: "16px"
                }}>
                  {successMessage}
                </div>
              )}
              <Button
                type="cta"
                className="verify-button"
                style={buttonStyles}
                action={attemptVerification}
                disabled={!verificationEnabled || !!successMessage}
              >
                {buttonContents}
              </Button>
            </div>
            <div className="no-account">
              <span>Didn't get an email?</span>
              <Button type="cta" className="sign-up-button" style={buttonStyles} action={resendCode}>
                Resend Code
              </Button>
            </div>
          </div>
        </div>
      </div>
      {isMobile ? null : instructions}
    </div>
  );
});

export default VerifyAccount;
